"use client";

import { useLocaleRouter } from "@i18n/routing";
import { type PublicDomainFailure, parsePublicDomain } from "@repo/core/client";
import { Button } from "@ui/components/button";
import { Input } from "@ui/components/input";
import { AlertCircleIcon, Loader2Icon, SearchIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState, useTransition } from "react";

interface CommonCrawlRankFormProps {
	/** Prefills the input on a per-domain page so the URL and the field agree. */
	initialDomain?: string;
}

/**
 * Maps a shared validator failure to this form's translation key. The parsing
 * itself lives in @repo/core so the API handler and the per-domain page accept
 * exactly the same inputs.
 */
const VALIDATION_MESSAGE_BY_REASON = {
	empty: "marketing.commonCrawlRankChecker.form.validation.enterDomain",
	reserved: "marketing.commonCrawlRankChecker.form.validation.reservedDomain",
	notListed:
		"marketing.commonCrawlRankChecker.form.validation.notListedDomain",
	invalid: "marketing.commonCrawlRankChecker.form.validation.invalidDomain",
} as const satisfies Record<PublicDomainFailure, string>;

/**
 * The form navigates rather than fetching. Every lookup now has its own URL
 * (`/common-crawl-rank-checker/example.com`), which is what makes a result
 * shareable, indexable, and cacheable. Submitting is therefore a route change,
 * and the server component behind that route reads the web graph.
 */
export function CommonCrawlRankForm({
	initialDomain = "",
}: CommonCrawlRankFormProps) {
	const t = useTranslations();
	const router = useLocaleRouter();
	const [domain, setDomain] = useState(initialDomain);
	const [validationError, setValidationError] = useState<string | null>(null);
	const [isPending, startTransition] = useTransition();

	const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setDomain(e.target.value);

		// Clear validation error when user types
		if (validationError) {
			setValidationError(null);
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		// Validate and normalize the domain with the shared parser
		const validation = parsePublicDomain(domain);

		if (!validation.ok) {
			setValidationError(
				t(VALIDATION_MESSAGE_BY_REASON[validation.reason]),
			);
			return;
		}

		setValidationError(null);
		startTransition(() => {
			router.push(
				`/common-crawl-rank-checker/${encodeURIComponent(validation.domain)}`,
			);
		});
	};

	return (
		<form
			className="flex flex-col gap-4 justify-center max-w-2xl mx-auto"
			onSubmit={handleSubmit}
		>
			<div className="flex flex-col sm:flex-row gap-4">
				<div className="flex-1">
					<div className="relative">
						<Input
							placeholder={t(
								"marketing.commonCrawlRankChecker.form.placeholder",
							)}
							value={domain}
							onChange={handleDomainChange}
							data-testid="domain-input"
							disabled={isPending}
							className={`h-14 text-lg px-4 pr-12 transition-colors ${
								validationError
									? "border-red-300 focus:border-red-500 focus:ring-red-500 bg-red-50/50"
									: ""
							}`}
						/>
						{validationError && (
							<AlertCircleIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500" />
						)}
					</div>
					{validationError && (
						<p className="mt-2 text-sm text-red-600 flex items-center gap-1">
							<AlertCircleIcon className="w-4 h-4" />
							{validationError}
						</p>
					)}
				</div>

				<div className="flex items-start">
					<Button
						type="submit"
						variant="primary"
						disabled={isPending}
						size="lg"
						className="h-14 px-8 text-lg font-medium"
					>
						{isPending ? (
							<>
								<Loader2Icon className="w-5 h-5 mr-2 animate-spin" />
								{t(
									"marketing.commonCrawlRankChecker.form.checking",
								)}
							</>
						) : (
							<>
								<SearchIcon className="w-5 h-5 mr-2" />
								{t(
									"marketing.commonCrawlRankChecker.form.submit",
								)}
							</>
						)}
					</Button>
				</div>
			</div>

			<p className="text-sm text-muted-foreground text-center">
				{t("marketing.commonCrawlRankChecker.form.hint")}
			</p>
		</form>
	);
}
